import React, { useState, useEffect } from 'react';
import TimesheetGrid from '../timesheets/timesheet-grid';
import Index from '../index';

interface ProjectAssignment {
  ProjectId: string;
  ProjectTitle: string;
  Status: string;
  RoleOnProject: string;
}

interface Timesheet {
  TimesheetId: string;
  WeekStartDate: string;
  Status: string;
  TotalHours: number;
  SubmittedDate?: string;
}

export default function TeamMemberHomepage() {
  const [projects, setProjects] = useState<ProjectAssignment[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const authEmail = localStorage.getItem('auth_email');
      if (!authEmail) throw new Error('Not logged in');

      // Get user info
      const userRes = await fetch(`/users/me?email=${encodeURIComponent(authEmail)}`);
      const userData = await userRes.json();
      if (!userData.success) throw new Error('Could not fetch user');

      const userId = userData.data.UserId;

      // Get user's team(s) - team members are assigned to teams
      const teamsRes = await fetch(`/teams/members?userId=${encodeURIComponent(userId)}`);
      const teamsData = await teamsRes.json();
      const userTeams = (teamsData.data || []).map((t: any) => t.TeamId);

      if (userTeams.length === 0) {
        // User not assigned to any team, no projects to show
        setProjects([]);
      } else {
        // Get projects assigned to user's teams
        const teamIdList = userTeams.join(',');
        const projRes = await fetch(`/projects/by-teams/${teamIdList}`);
        const projData = await projRes.json();

        // Filter to active projects
        const activeProjects = (projData.data || []).filter(
          (p: any) => p.Status === 'InDelivery' || p.Status === 'ReadyForDelivery'
        );

        setProjects(activeProjects.map((p: any) => ({
          ProjectId: p.ProjectId,
          ProjectTitle: p.Title,
          Status: p.Status,
          RoleOnProject: 'Contributor',
        })));
      }

      // Get user's timesheets
      const tsRes = await fetch(`/timesheets/${userId}`);
      const tsData = await tsRes.json();
      if (tsData.success) {
        setTimesheets(tsData.data || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading your dashboard...</div>;
  }

  if (showPortfolio) {
    return (
      <div>
        <Index />
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <button className="btn-secondary" onClick={() => setShowPortfolio(false)}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 20px' }}>
        {error && (
          <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '2rem' }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setShowTimesheet(true)}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            + Submit Timesheet
          </button>
        </div>

        {/* Instructions Section */}
        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#e7f3ff', borderRadius: '4px', borderLeft: '4px solid #0066cc' }}>
          <h2 style={{ marginTop: 0, color: '#0066cc' }}>How to Submit Timesheets</h2>
          <ol style={{ color: '#333', lineHeight: '1.8' }}>
            <li>Click the <strong>"+ Submit Timesheet"</strong> button above</li>
            <li>Add your assigned projects or work categories (Admin, Holiday, Sickness, BAU)</li>
            <li>Enter hours for each day (Monday-Sunday)</li>
            <li>Mix projects and work categories as needed</li>
            <li>Submit your complete weekly timesheet</li>
          </ol>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
            💡 <strong>Tip:</strong> You can log partial days across different projects or categories. Your team lead will review and approve your submission.
          </p>
        </div>

        {/* Assigned Projects Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2>Your Assigned Projects</h2>
          {projects.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              color: '#666',
            }}>
              <p>No active projects assigned yet. Check with your team lead.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {projects.map(project => (
                <div key={project.ProjectId} style={{
                  padding: '1.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{project.ProjectTitle}</h3>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                    <strong>Status:</strong> {project.Status}
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                    <strong>Role:</strong> {project.RoleOnProject}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Timesheets Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2>Your Submitted Timesheets</h2>
          {timesheets.length === 0 ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              color: '#666',
            }}>
              <p>No timesheets submitted yet. Click the button above to submit your first timesheet.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Week of</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Hours</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheets.map(ts => (
                    <tr key={ts.TimesheetId} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.75rem' }}>
                        {new Date(ts.WeekStartDate).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {ts.TotalHours}h
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          backgroundColor: ts.Status === 'Approved' ? '#d4edda' :
                                          ts.Status === 'Submitted' ? '#fff3cd' : '#e2e3e5',
                          color: ts.Status === 'Approved' ? '#155724' :
                                ts.Status === 'Submitted' ? '#856404' : '#383d41',
                        }}>
                          {ts.Status}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#666' }}>
                        {ts.SubmittedDate ? new Date(ts.SubmittedDate).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showTimesheet && (
          <TimesheetGrid
            onSuccess={() => {
              setShowTimesheet(false);
              alert('Timesheet submitted successfully!');
              fetchData(); // Refresh data
            }}
            onCancel={() => setShowTimesheet(false)}
          />
        )}
      </div>
  );
}
